import type { FieldKey, ViewColumn, ViewContent, ViewSort } from '../views/types';
import type { FieldMeta } from './columnModel';

/**
 * Permission-protected fields (prices, …).
 *
 * A field the user may not see is neither listed nor rendered — but it is not *removed*. A tab is
 * shared by colleagues with different rights, and a user can regain a permission, so a forbidden
 * field stays in the stored columns exactly where it was. The working copy therefore always holds
 * the full columns; only what is shown is filtered. Edits are applied to the full columns, translated
 * from the positions the user sees, so saving can never drop what the user could not see.
 *
 * The real protection is the backend not sending the data; this only keeps the UI honest about it.
 */

export type IsAllowed = (key: FieldKey) => boolean;

/** Allowed unless the field's flag is present and not granted. Unknown keys are not allowed. */
export const fieldAccess = (fields: FieldMeta[]): IsAllowed => {
  const byKey = new Map(fields.map(field => [field.key, field]));
  return key => {
    const field = byKey.get(key);
    return !!field && (!field.secured || field.secured.granted);
  };
};

/** The columns as the user sees them: forbidden members left out, columns with nothing left gone. */
export const visibleColumns = (columns: ViewColumn[], isAllowed: IsAllowed): ViewColumn[] =>
  columns.map(column => ({ ...column, members: column.members.filter(isAllowed) })).filter(column => column.members.length > 0);

/** A preset as this user gets it — "Tender" without the price. Empty when nothing of it is visible. */
export const visiblePreset = (members: FieldKey[], isAllowed: IsAllowed): FieldKey[] => members.filter(isAllowed);

/**
 * Presets as this user gets them, without the ones that have nothing visible left. Columns are
 * matched against these: a "Tender" column is still "Tender" for someone who can't see its price.
 */
export const visiblePresets = <P extends { members: FieldKey[] }>(presets: P[], isAllowed: IsAllowed): P[] =>
  presets.map(preset => ({ ...preset, members: visiblePreset(preset.members, isAllowed) })).filter(preset => preset.members.length > 0);

/**
 * Converts a gap position among the *visible* columns (what a drop target reports) into one among the
 * full columns. A gap before visible column `i` is placed right before that column; the gap after the
 * last visible column goes to the very end.
 */
export const toFullGapIndex = (columns: ViewColumn[], visibleGap: number, isAllowed: IsAllowed): number => {
  const visibleIds = visibleColumns(columns, isAllowed).map(column => column.id);
  if (visibleGap >= visibleIds.length) return columns.length;
  const fullIndex = columns.findIndex(column => column.id === visibleIds[Math.max(0, visibleGap)]);
  return fullIndex === -1 ? columns.length : fullIndex;
};

/**
 * Converts a member position within a column as the user sees it into one in the stored column:
 * the field lands right before the visible member that was at that position (or after the last one).
 */
export const toFullMemberIndex = (column: ViewColumn, visibleIndex: number, isAllowed: IsAllowed): number => {
  const visible = column.members.filter(isAllowed);
  if (visibleIndex >= visible.length) {
    const last = visible[visible.length - 1];
    return last === undefined ? column.members.length : column.members.indexOf(last) + 1;
  }
  return column.members.indexOf(visible[Math.max(0, visibleIndex)]);
};

/** "Remove column" from the sidebar removes what the user sees; forbidden members keep their column. */
export const removeVisibleColumn = (columns: ViewColumn[], id: string, isAllowed: IsAllowed): ViewColumn[] =>
  columns.map(column => (column.id === id ? { ...column, members: column.members.filter(key => !isAllowed(key)) } : column)).filter(column => column.members.length > 0);

/**
 * A copy belongs to the user who made it and holds only what they can see — otherwise a field they
 * never chose would appear in "their" tab the day they get the permission.
 */
export const withoutForbidden = (content: ViewContent, isAllowed: IsAllowed, allowedSortFields: string[], defaultSort: ViewSort): ViewContent => ({
  ...content,
  columns: visibleColumns(content.columns, isAllowed),
  sort: effectiveSort(content.sort, allowedSortFields, defaultSort),
});

/**
 * The sort the list actually requests. A tab sorted by a field this user may not see falls back to
 * the table's default — ordering rows by price reveals the prices' order.
 */
export const effectiveSort = (sort: ViewSort, allowedSortFields: string[], defaultSort: ViewSort): ViewSort =>
  sort && allowedSortFields.includes(sort.sortField) ? sort : defaultSort;
